const BRANDS = ['Haiwell', 'Supmea', 'Savch', 'Yotta Control'];

/**
 * Reusable modal for selecting approved brands + per-brand discounts.
 * Used both when approving a new application and editing an existing partner.
 */
const BrandAccessModal = ({
  open,
  title,
  subject, // { label1, value1, label2, value2 }
  approvedBrands,
  toggleBrand,
  brandDiscounts,
  setBrandDiscounts,
  onConfirm,
  onCancel,
  confirmLabel,
  infoText,
}) => {
  if (!open) return null;

  const canSubmit = approvedBrands.length > 0;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      data-testid="brand-access-modal"
    >
      <div className="bg-ewss-navy border border-gray-700 rounded-xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-white mb-6">{title}</h2>

        {subject && (
          <div className="mb-6 p-4 bg-black/40 rounded-lg">
            <p className="text-gray-400 text-sm mb-1">{subject.label1}</p>
            <p className="text-white font-semibold">{subject.value1}</p>
            <p className="text-gray-400 text-sm mt-2 mb-1">{subject.label2}</p>
            <p className="text-white">{subject.value2}</p>
          </div>
        )}

        {/* Brand Access */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">Select Approved Brands *</h3>
          <div className="space-y-3">
            {BRANDS.map((brand) => (
              <label
                key={brand}
                className="flex items-center gap-3 p-3 bg-black/40 rounded-lg cursor-pointer hover:bg-black/60"
              >
                <input
                  type="checkbox"
                  checked={approvedBrands.includes(brand)}
                  onChange={() => toggleBrand(brand)}
                  className="w-5 h-5"
                  data-testid={`brand-access-checkbox-${brand}`}
                />
                <span className="text-white font-medium">{brand}</span>
              </label>
            ))}
          </div>
          {!canSubmit && (
            <p className="text-red-400 text-sm mt-2">⚠️ Select at least one brand</p>
          )}
        </div>

        {/* Discounts */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">Set Brand Discounts (%)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {BRANDS.map((brand) => (
              <div key={brand}>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {brand} Discount
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={brandDiscounts[brand]}
                  onChange={(e) =>
                    setBrandDiscounts({
                      ...brandDiscounts,
                      [brand]: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="w-full px-4 py-2 bg-black/40 border border-gray-600 rounded-lg text-white"
                  data-testid={`brand-access-discount-${brand}`}
                />
              </div>
            ))}
          </div>
          {infoText && <p className="text-gray-400 text-sm mt-2">{infoText}</p>}
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-end">
          <button
            onClick={onCancel}
            className="px-6 py-3 border border-gray-600 text-white rounded-lg hover:bg-white/5"
            data-testid="brand-access-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canSubmit}
            className={`px-6 py-3 rounded-lg font-semibold ${
              !canSubmit
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-ewss-cyan text-ewss-navy hover:bg-ewss-cyan/90'
            }`}
            data-testid="brand-access-confirm"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandAccessModal;
